/**
 * Automatische Akkorderkennung.
 *
 * Ziel: Man schreibt einfach, was man ohnehin auf ein Blatt schreiben würde.
 * Keine eckigen Klammern, keine geschweiften. Die App erkennt selbst, ob eine
 * Zeile Akkorde enthält oder Text ist.
 *
 *   Intro: ebm, Eb Eb, dm7, D,     -> Taktraster mit vier Takten
 *   Ebm        Ab                  -> Akkordzeile ...
 *   Montag früh im Proberaum       -> ... über dieser Textzeile
 *
 * Die Erkennung ist bewusst streng: Ein Wort gilt nur als Akkord, wenn es
 * aus einem Grundton und einer bekannten Erweiterung besteht. Und eine Zeile
 * gilt nur dann als Akkordzeile, wenn JEDES Wort darin ein Akkord ist.
 * Dadurch bleibt "Am Montag früh" eine Textzeile, obwohl "Am" ein Akkord wäre.
 */

/** Grundton: a bis g, dazu Vorzeichen. */
const ROOT = '[A-Ga-g](?:##|bb|[#b♯♭])?';

/**
 * Erweiterung: nur bekannte Bausteine, damit Wörter wie "Abend" oder "Ende"
 * nicht als Akkord durchgehen.
 */
const QUALITY =
  '(?:maj|min|dim|aug|sus|add|alt|dom|omit|no|[Mm]|[+\\-]|[#b♯♭]|\\d{1,2}|[()°øΔ])*';

const CHORD_RE = new RegExp(`^${ROOT}${QUALITY}(?:/${ROOT})?$`);

/** Zeichen, die "Akkord gilt weiter" bedeuten. */
const HOLD = new Set(['-', '–', '—', '~', '%', '/', '.', '·']);

/** Wiederholungsangabe wie "4x" oder "x4". */
const REPEAT_RE = /^(?:(\d{1,3})\s*[xX]|[xX]\s*(\d{1,3}))$/;

export function isHoldToken(token: string): boolean {
  return HOLD.has(token);
}

export function isRepeatToken(token: string): boolean {
  return REPEAT_RE.test(token);
}

/** Ist das Wort ein Akkord oder ein Haltezeichen? */
export function isChordToken(token: string): boolean {
  if (token === '') return false;
  if (isHoldToken(token)) return true;
  return CHORD_RE.test(token);
}

/**
 * Schreibt den Grundton gross, den Rest unverändert.
 * Aus "ebm" wird "Ebm", aus "dm7" wird "Dm7", aus "c/e" wird "C/E".
 */
export function normalizeChord(token: string): string {
  if (isHoldToken(token)) return token;
  if (!CHORD_RE.test(token)) return token;

  const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
  const slash = token.indexOf('/');
  // Kein Schrägstrich, oder er steht am Anfang: nur vorne gross schreiben.
  if (slash <= 0) return upperFirst(token);
  return upperFirst(token.slice(0, slash)) + '/' + upperFirst(token.slice(slash + 1));
}

/**
 * Prüft, ob eine ganze Zeile aus Akkorden besteht.
 * Eine abschliessende Wiederholungsangabe ist erlaubt.
 */
export function isChordLine(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === '') return false;

  const tokens = trimmed.split(/[\s,]+/).filter(Boolean);
  // Eine Zeile aus lauter Kommas ist ein Raster aus leeren Takten,
  // zum Beispiel "Instr.: , , , ,".
  if (tokens.length === 0) return trimmed.includes(',');

  const last = tokens[tokens.length - 1] as string;
  const body = isRepeatToken(last) ? tokens.slice(0, -1) : tokens;
  if (body.length === 0) return false;

  return body.every(isChordToken);
}

/** Trennt die Wiederholungsangabe am Zeilenende ab. */
export function splitRepeat(text: string): { body: string; repeat: string | null } {
  const trimmed = text.trim();
  const match = /\s([0-9]{1,3}\s*[xX]|[xX]\s*[0-9]{1,3})$/.exec(trimmed);
  if (!match) return { body: trimmed, repeat: null };
  return {
    body: trimmed.slice(0, match.index).trim(),
    repeat: (match[1] as string).replace(/\s+/g, ''),
  };
}

/**
 * Zerlegt eine Akkordzeile in Takte.
 *
 *   ein Komma      neuer Takt          Am, Dm     ->  |Am|Dm|
 *   zwei Kommas    derselbe Takt       Am,, Dm    ->  |Am Dm|
 *   Leerzeichen    derselbe Takt       Am Dm,     ->  |Am Dm|
 *
 * Das Doppelkomma sagt also dasselbe wie das Leerzeichen, nur ausdrücklich.
 * Es ist für den Fall da, dass man beim Schreiben ohnehin in Kommas denkt und
 * dann merkt: der Akkord gehört noch in DIESEN Takt. Man muss nichts
 * umstellen, man tippt einfach ein Komma mehr.
 *
 * Ohne jedes Komma bekommt jeder Akkord einen eigenen Takt – so bleibt
 * "Ebm Ab" das, was man beim Hinschreiben meint: zwei Takte.
 *
 * Absichtlich als Scanner und nicht als split(','): nur so lässt sich ein
 * Komma von zweien unterscheiden, und nur so bleibt die alte Bedeutung von
 * Kommaketten erhalten (siehe unten).
 */
export function splitBars(text: string): string[][] {
  if (!text.includes(',')) {
    return text
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((token) => [normalizeChord(token)]);
  }

  const bars: string[][] = [];
  let bar: string[] = [];
  let word = '';

  /** Das angefangene Wort in den laufenden Takt legen. */
  const takeWord = () => {
    if (word === '') return;
    bar.push(normalizeChord(word));
    word = '';
  };

  /** Den laufenden Takt abschliessen und einen neuen beginnen. */
  const closeBar = () => {
    takeWord();
    bars.push(bar);
    bar = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i] as string;

    if (ch === ',') {
      // Wie viele Kommas stehen hier hintereinander?
      let run = 1;
      while (text[i + run] === ',') run += 1;
      takeWord();
      /*
       * Zwei oder mehr Kommas hängen an: der Takt bleibt offen, der nächste
       * Akkord landet darin. Das gilt aber nur, wenn im Takt schon etwas
       * steht – eine Kette aus lauter Kommas ("Instr.: ,,,,") meint nach wie
       * vor leere Takte und nicht einen einzigen offenen.
       */
      if (run >= 2 && bar.length > 0) {
        i += run - 1;
        continue;
      }
      for (let k = 0; k < run; k += 1) closeBar();
      i += run - 1;
      continue;
    }

    if (/\s/.test(ch)) {
      takeWord();
      continue;
    }
    word += ch;
  }

  // Was nach dem letzten Komma noch kommt, ist ein eigener Takt. Steht dort
  // nichts, war das Komma nur der Abschluss der Zeile und kein Leertakt.
  takeWord();
  if (bar.length > 0) bars.push(bar);
  return bars;
}

/** Ein Akkord mit seiner Spalte in der Zeile. */
export interface ChordAt {
  column: number;
  chord: string;
}

/** Findet alle Akkorde einer Zeile samt ihrer Spalte. */
export function chordPositions(raw: string): ChordAt[] {
  const out: ChordAt[] = [];
  const re = /\S+/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw)) !== null) {
    out.push({ column: match.index, chord: normalizeChord(match[0]) });
  }
  return out;
}
